/**
 * The short note on a like or flame (33 letters, Masco 2026-09-13).
 *
 * Rules, enforced HERE rather than in the input:
 *   1. 1–33 letters. `isCommentValid` in weights.ts is the single judge.
 *   2. One comment per person. Immutable once sent — you get one opener.
 *   3. A comment never travels alone; the caller sends the heart or flame with it.
 *
 * Storage: un8.matching.comments.v1
 */

import { writeLocal } from "../storage/localWrite";
import { COMMENT_MAX_LETTERS, countLetters, isCommentValid } from "./weights";

const KEY = "un8.matching.comments.v1";

/** Which part of the profile the comment hangs on. */
export type CommentAnchor = "photo" | "about" | "lookingFor" | "hobbies";

/** The words (or empty for photo) a like is attached to. */
export function promptText(
  profile: { aboutYou?: string; lookingFor?: string; hobbies?: string },
  anchor: CommentAnchor
): string {
  if (anchor === "about") return (profile.aboutYou ?? "").trim();
  if (anchor === "lookingFor") return (profile.lookingFor ?? "").trim();
  if (anchor === "hobbies") return (profile.hobbies ?? "").trim();
  return "";
}

export type ProfileComment = {
  fromUserId: string;
  toProfileId: string;
  anchorId: CommentAnchor;
  text: string;
  /** ISO timestamp. */
  sentAt: string;
};

/** fromUserId -> toProfileId -> comment */
type MapFile = Record<string, Record<string, ProfileComment>>;

function readAll(): MapFile {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    return JSON.parse(raw) as MapFile;
  } catch {
    return {};
  }
}

function writeAll(map: MapFile) {
  writeLocal(KEY, JSON.stringify(map));
}

export type SendCommentError = "too_long" | "empty" | "already_sent";

export const COMMENT_STORAGE_KEY = KEY;

export const commentStore = {
  MAX_LETTERS: COMMENT_MAX_LETTERS,
  MAX_WORDS: COMMENT_MAX_LETTERS,
  countLetters,
  isValid: isCommentValid,

  /** The comment this user sent to that profile, if any. */
  get(fromUserId: string, toProfileId: string): ProfileComment | null {
    return readAll()[fromUserId]?.[toProfileId] ?? null;
  },

  /** Every comment this user has sent. Newest first. */
  listSent(fromUserId: string): ProfileComment[] {
    const byTarget = readAll()[fromUserId] ?? {};
    return Object.values(byTarget).sort((a, b) => b.sentAt.localeCompare(a.sentAt));
  },

  /**
   * Every comment addressed TO this profile. Local-only mirror: in the closed
   * test each device holds its own writer, so this reads across the map rather
   * than assuming one author.
   */
  listReceived(toProfileId: string): ProfileComment[] {
    const out: ProfileComment[] = [];
    for (const byTarget of Object.values(readAll())) {
      const c = byTarget[toProfileId];
      if (c) out.push(c);
    }
    return out.sort((a, b) => b.sentAt.localeCompare(a.sentAt));
  },

  /**
   * Record a comment. Rejects over-length text even when called straight from
   * the console — this is the enforcement point, not the composer.
   */
  send(input: {
    fromUserId: string;
    toProfileId: string;
    anchorId: CommentAnchor;
    text: string;
    now?: Date;
  }): { ok: true; comment: ProfileComment } | { ok: false; error: SendCommentError } {
    const text = input.text.trim();
    if (countLetters(text) === 0) return { ok: false, error: "empty" };
    if (!isCommentValid(text)) return { ok: false, error: "too_long" };

    const map = readAll();
    const byTarget = map[input.fromUserId] ?? {};
    if (byTarget[input.toProfileId]) {
      return { ok: false, error: "already_sent" };
    }

    const comment: ProfileComment = {
      fromUserId: input.fromUserId,
      toProfileId: input.toProfileId,
      anchorId: input.anchorId,
      text,
      sentAt: (input.now ?? new Date()).toISOString(),
    };
    byTarget[input.toProfileId] = comment;
    map[input.fromUserId] = byTarget;
    writeAll(map);
    return { ok: true, comment };
  },

  clearForUser(fromUserId: string) {
    const map = readAll();
    delete map[fromUserId];
    writeAll(map);
  },

  STORAGE_KEY: KEY,
};
